/**
 * @fileoverview 축 멤버의 합계열 여부를 토글하는 컴포넌트 (AxisSumToggle)
 */

import { cn } from "@/lib/utils";
import { Button } from "@/styles/ui/button";
import { Sigma } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { patchTableNotUsedSumTagKeys } from "@/apis/table/route";
import { GetTableNotUsedSumTagKeysResponse } from "@/apis/table/types";
import { useWorkbookIdAndTableId } from "../../hook";
import queries from "@/queries";
import { useNotUsedSumTagKeys } from "../../table/hook";

/**
 * 축 멤버의 합계열 여부를 토글하는 컴포넌트
 */
export const AxisSumToggle = ({
  memberTagKey,
  roleKey,
}: {
  memberTagKey: string;
  roleKey: string;
}) => {
  const { data: notUsedSumTagKeysData } = useNotUsedSumTagKeys();
  const scopedKey = `${roleKey}:${memberTagKey}`;
  const isSumToggled = !notUsedSumTagKeysData?.notUsedSumTagKeys.some(
    (key) => key === memberTagKey || key === scopedKey,
  );
  const queryClient = useQueryClient();

  // 쓸 때는 항상 role-scoped 포맷(roleKey:axisKey)으로 저장해 표 간 합계 연동 버그를 방지한다.
  // 읽을 때는 legacy bare key도 비활성으로 취급(위 isSumToggled 참고).
  const newNotUsedSumTagKeys = (): string[] => {
    const currentKeys = notUsedSumTagKeysData?.notUsedSumTagKeys ?? [];
    const isCurrentlyDisabled = currentKeys.some(
      (key) => key === memberTagKey || key === scopedKey,
    );
    if (isCurrentlyDisabled) {
      return currentKeys.filter(
        (key) => key !== memberTagKey && key !== scopedKey,
      );
    }
    return [...currentKeys, scopedKey];
  };

  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();

  const notUsedSumTagKeysQueryKey = queries
    .workbook(workbookId!)
    ._ctx.tables(tableId!)
    ._ctx.notUsedSumTagKeys().queryKey;

  const { mutate: toggleNotUsedSumTagKeyMutation } = useMutation({
    mutationFn: async (newNotUsedSumTagKeys: string[]) => {
      await patchTableNotUsedSumTagKeys(workbookId!, tableId!, {
        notUsedSumTagKeys: newNotUsedSumTagKeys,
      });
    },
    onMutate: async (newNotUsedSumTagKeys: string[]) => {
      await queryClient.cancelQueries({
        queryKey: notUsedSumTagKeysQueryKey,
      });

      const previousResponse = queryClient.getQueryData(
        notUsedSumTagKeysQueryKey,
      );
      queryClient.setQueryData(
        notUsedSumTagKeysQueryKey,
        (old: GetTableNotUsedSumTagKeysResponse) => {
          return {
            ...old,
            notUsedSumTagKeys: newNotUsedSumTagKeys,
          };
        },
      );
      return { previousResponse };
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(
        notUsedSumTagKeysQueryKey,
        context?.previousResponse,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: notUsedSumTagKeysQueryKey,
      });
    },
  });

  return (
    <div className="flex items-center flex-shrink-0 gap-[8px] justify-end">
      <span className="caption-1-medium text-[#737373]">합계열</span>
      <Button
        onClick={() => toggleNotUsedSumTagKeyMutation(newNotUsedSumTagKeys())}
        className={cn(
          "overflow-clip relative rounded-[100px] w-[44px] h-[24px] transition-all duration-200 ",
          "hover:bg-neutral-300 ",
          // 토글 상태에 따른 배경색 변경
          isSumToggled ? "bg-orange-200 hover:bg-orange-300" : "bg-neutral-200",
        )}
        data-name="합계 토글버튼"
      >
        {/* 토글 스위치의 슬라이더 */}
        <div
          className={cn(
            "absolute left-0.5 overflow-clip rounded-[100px]  top-1/2 translate-y-[-50%] transition-transform duration-200 flex items-center w-[20px] h-[20px] justify-center",
            isSumToggled
              ? "translate-x-[20px] bg-orange-500"
              : "bg-neutral-400",
          )}
        >
          <Sigma className={cn("p-[3px] text-white")} />
        </div>
      </Button>
    </div>
  );
};
